import { Stack } from '@fluentui/react';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';
import {
  EntityTagPurchaseOrderCreate,
  EntityTagPurchaseOrderCreateVariables,
} from 'common/graphql/__generated__/EntityTagPurchaseOrderCreate';
import {
  ListPurchaseOrderTags,
  ListPurchaseOrderTagsVariables,
} from 'common/graphql/__generated__/ListPurchaseOrderTags';
import {
  PurchaseOrderAvailableTags,
  PurchaseOrderAvailableTagsVariables,
} from 'common/graphql/__generated__/PurchaseOrderAvailableTags';
import { EntityTagInput } from 'common/types/globalTypes';
import React from 'react';
import { useStyles } from './index.styles';

import ENTITY_PURCHASE_ORDER_CREATE from '../../../../../../graphql/EntityTagPurchaseOrderCreate.graphql';
import PURCHASE_ORDER_AVAILABLE_TAGS from '../../../../../../graphql/PurchaseOrderAvailableTags.graphql';
import LIST_PURCHASE_ORDER_TAGS from '../../../../../../graphql/ListPurchaseOrderTags.graphql';
import ENTITY_TAG_DELETE from '../../../../../../graphql/EntityTagDelete.graphql';

interface TagsProps {
  purchaseOrderId: string;
}

export const Tags: React.FC<TagsProps> = ({ purchaseOrderId }) => {
  const styles = useStyles();

  // Memoized functions for extracting data and generating variables
  const extractAvailableTags = React.useCallback(
    (data: PurchaseOrderAvailableTags | undefined) =>
      data?.purchaseOrderAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? [],
    []
  );

  const extractEntityTags = React.useCallback(
    (data: ListPurchaseOrderTags | undefined) =>
      data?.purchaseOrder?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown',
      })) ?? [],
    []
  );

  const getAvailableTagsVars = React.useCallback(
    (entityId: string): PurchaseOrderAvailableTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const getEntityTagsVars = React.useCallback(
    (entityId: string): ListPurchaseOrderTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const createTagsVars = React.useCallback(
    (tags: EntityTagInput[]): EntityTagPurchaseOrderCreateVariables => ({
      input: {
        entityTag: tags,
      },
    }),
    []
  );

  const deleteTagsVars = React.useCallback(
    (id: string, rowTimestamp: string): EntityTagDeleteVariables => ({
      entityTagDeleteInput: {
        entityDelete: [{ id, rowTimestamp }],
      },
    }),
    []
  );

  return (
    <Stack tokens={{ childrenGap: 15, padding: '24px 25px 0px' }}>
      <EntityTagsSection<
        PurchaseOrderAvailableTags,
        PurchaseOrderAvailableTagsVariables,
        ListPurchaseOrderTags,
        ListPurchaseOrderTagsVariables,
        EntityTagPurchaseOrderCreate,
        EntityTagPurchaseOrderCreateVariables,
        EntityTagDelete,
        EntityTagDeleteVariables
      >
        entityId={purchaseOrderId}
        getAvailableTagsGql={PURCHASE_ORDER_AVAILABLE_TAGS}
        getEntityTagsGql={LIST_PURCHASE_ORDER_TAGS}
        addTagGql={ENTITY_PURCHASE_ORDER_CREATE}
        deleteTagGql={ENTITY_TAG_DELETE}
        extractAvailableTags={extractAvailableTags}
        extractEntityTags={extractEntityTags}
        getAvailableTagsVars={getAvailableTagsVars}
        getEntityTagsVars={getEntityTagsVars}
        createTagsVars={createTagsVars}
        deleteTagsVars={deleteTagsVars}
      />
    </Stack>
  );
};
